"use client";

import React from "react";
import { toast } from "sonner";

import { Button } from "@soonlist/ui/button";

import {
  ArrowLeft,
  Check,
  Copy,
  ExternalLink,
  Laptop,
  Mail,
  Share,
  Smartphone,
} from "~/components/icons";

interface CalendarSyncClientProps {
  subscriptionUrl: string | null;
  appScheme: string | null;
}

export function CalendarSyncClient({
  subscriptionUrl,
  appScheme,
}: CalendarSyncClientProps) {
  const fromApp = appScheme !== null;
  const backHref = fromApp ? `${appScheme}://` : "/";

  return (
    <div className="mx-auto max-w-xl">
      <p className="text-xs font-bold uppercase tracking-widest text-interactive-1">
        Calendar sync
      </p>
      <h1 className="text-neutral-0 mt-2 font-heading text-3xl leading-tight sm:text-4xl">
        {subscriptionUrl
          ? "Add to Google Calendar"
          : "Sync your Soonlist to your calendar"}
      </h1>
      <p className="mt-3 text-base leading-relaxed text-neutral-1 sm:text-lg">
        {subscriptionUrl ? (
          <>
            <span className="md:hidden">
              Google Calendar setup needs a computer — send your link there in
              one tap.
            </span>
            <span className="hidden md:inline">
              Add your Soonlist link as a subscribed calendar to keep events in
              sync.
            </span>
          </>
        ) : (
          <>
            One link works in any calendar app — Apple Calendar, Google
            Calendar, Outlook, Fantastical. New Soonlist events show up
            automatically.
          </>
        )}
      </p>

      {subscriptionUrl ? (
        <>
          <SubscriptionLinkCard url={subscriptionUrl} />
          <MobileActions url={subscriptionUrl} />
          <DesktopActions url={subscriptionUrl} />
          <OnYourComputerCallout />
        </>
      ) : (
        <NoLinkFallback />
      )}

      <hr className="my-10 border-neutral-3" />

      <AppleCalendarSection
        subscriptionUrl={subscriptionUrl}
        webcalFromHttps={subscriptionUrl ? toWebcal(subscriptionUrl) : null}
      />

      <p className="mt-10 text-center text-sm text-neutral-2">
        Using Outlook, Fantastical, Proton, or something else? Look for{" "}
        <em>Subscribe from web</em> or <em>Add calendar from URL</em> and paste
        your link.
      </p>

      <div className="mt-12 flex justify-center">
        <Button asChild size="lg">
          <a href={backHref}>
            <ArrowLeft className="mr-2 size-4" />
            {fromApp ? "Back to Soonlist" : "Go to Soonlist"}
          </a>
        </Button>
      </div>
    </div>
  );
}

function SubscriptionLinkCard({ url }: { url: string }) {
  return (
    <div className="mt-8 rounded-2xl border border-neutral-3 bg-white p-5 shadow-sm">
      <p className="text-[11px] font-bold uppercase tracking-widest text-interactive-1">
        Your subscription link
      </p>
      <div className="text-neutral-0 mt-2 select-all break-all rounded-lg border border-neutral-3 bg-white px-3 py-2.5 font-mono text-[13px] leading-snug">
        {url}
      </div>
      <p className="mt-3 text-xs leading-snug text-neutral-2">
        Anyone with this link can subscribe to your events — keep it private.
      </p>
    </div>
  );
}

function MobileActions({ url }: { url: string }) {
  return (
    <div className="mt-4 grid gap-2 md:hidden">
      <ShareButton url={url} />
      <EmailButton url={url} />
      <CopyButton url={url} />
    </div>
  );
}

function DesktopActions({ url }: { url: string }) {
  return (
    <div className="mt-4 hidden gap-2 md:grid md:grid-cols-2">
      <CopyButton url={url} size="lg" />
      <Button asChild size="lg" variant="secondary">
        <a
          href="https://calendar.google.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          <ExternalLink className="mr-2 size-4" />
          Open Google Calendar
        </a>
      </Button>
    </div>
  );
}

function ShareButton({ url }: { url: string }) {
  const handleShare = async () => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- navigator.share may not exist in all browsers
    if (typeof navigator === "undefined" || !navigator.share) {
      try {
        await navigator.clipboard.writeText(url);
        toast("Link copied");
      } catch {
        toast("Couldn't open share — select the link manually");
      }
      return;
    }
    try {
      await navigator.share({
        title: "My Soonlist calendar link",
        text: "Subscribe in Google Calendar on your computer:",
        url,
      });
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") return;
      toast("Couldn't open share sheet");
    }
  };

  return (
    <button
      type="button"
      onClick={handleShare}
      className="flex w-full items-center gap-3 rounded-xl bg-interactive-1 px-4 py-3.5 text-left text-white shadow-sm transition-colors hover:bg-interactive-1/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-interactive-1 focus-visible:ring-offset-2"
    >
      <Share className="size-5 shrink-0" aria-hidden="true" />
      <span className="flex flex-col leading-tight">
        <span className="text-base font-semibold">Send to my computer</span>
        <span className="text-xs opacity-85">AirDrop, Mail, Messages…</span>
      </span>
    </button>
  );
}

function EmailButton({ url }: { url: string }) {
  const subject = "My Soonlist calendar link";
  const body = [
    "Open this on your computer to subscribe in Google Calendar:",
    "",
    url,
    "",
    "1. Open https://calendar.google.com",
    "2. In the sidebar, hover Other calendars → click + → From URL",
    "3. Paste the link above, click Add calendar",
  ].join("\n");
  const href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  return (
    <Button asChild size="lg" variant="secondary">
      <a href={href}>
        <Mail className="mr-2 size-4" />
        Email link to myself
      </a>
    </Button>
  );
}

function CopyButton({
  url,
  size = "default",
}: {
  url: string;
  size?: "default" | "lg";
}) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast("Link copied");
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      toast("Couldn't copy — select the link manually");
    }
  };

  return (
    <Button
      onClick={handleCopy}
      size={size}
      variant={size === "lg" ? "default" : "outline"}
    >
      {copied ? (
        <Check className="mr-2 size-4" aria-hidden="true" />
      ) : (
        <Copy className="mr-2 size-4" aria-hidden="true" />
      )}
      {copied ? "Copied" : "Copy link"}
    </Button>
  );
}

function OnYourComputerCallout() {
  return (
    <>
      <div className="mt-4 rounded-2xl border border-neutral-3 bg-white p-5 shadow-sm md:hidden">
        <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-interactive-1">
          <Laptop className="size-3.5" aria-hidden="true" />
          Then on your computer
        </p>
        <p className="mt-2 text-sm leading-relaxed text-neutral-1">
          Open <strong className="text-neutral-0">calendar.google.com</strong>,
          hover <strong className="text-neutral-0">Other calendars</strong> in
          the sidebar → click <strong className="text-neutral-0">+</strong> →{" "}
          <strong className="text-neutral-0">From URL</strong>, paste the link,
          click <strong className="text-neutral-0">Add calendar</strong>.
        </p>
      </div>
      <ol className="mt-6 hidden list-none space-y-3 md:block">
        <Step n={1}>
          In Google Calendar, hover{" "}
          <strong className="text-neutral-0">Other calendars</strong> → click{" "}
          <strong className="text-neutral-0">+</strong> →{" "}
          <strong className="text-neutral-0">From URL</strong>.
        </Step>
        <Step n={2}>
          Paste the link, click{" "}
          <strong className="text-neutral-0">Add calendar</strong>. Done —
          Google syncs every ~24h.
        </Step>
      </ol>
    </>
  );
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="relative pl-10 text-neutral-1">
      <span className="absolute left-0 top-0 flex size-7 items-center justify-center rounded-full bg-interactive-2 text-sm font-bold text-interactive-1">
        {n}
      </span>
      {children}
    </li>
  );
}

function NoLinkFallback() {
  return (
    <div className="mt-8 rounded-2xl border border-dashed border-interactive-2 bg-interactive-3 p-5">
      <p className="text-neutral-0 text-sm font-semibold">
        To get your subscription link
      </p>
      <p className="mt-1 text-sm leading-relaxed text-neutral-1">
        Open Soonlist on your phone →{" "}
        <em>Settings → Sync your calendar → How to sync Google Calendar</em>.
        That opens this page with your link already filled in.
      </p>
    </div>
  );
}

function AppleCalendarSection({
  subscriptionUrl,
  webcalFromHttps,
}: {
  subscriptionUrl: string | null;
  webcalFromHttps: string | null;
}) {
  return (
    <section>
      <p className="inline-flex items-center gap-1.5 rounded-full bg-interactive-3 px-2.5 py-1 text-[11px] font-bold uppercase tracking-widest text-interactive-1">
        <Smartphone className="size-3 md:hidden" aria-hidden="true" />
        <Laptop className="hidden size-3 md:inline" aria-hidden="true" />
        <span className="md:hidden">Works on your phone</span>
        <span className="hidden md:inline">On your Mac</span>
      </p>
      <h2 className="text-neutral-0 mt-2 font-heading text-2xl">
        Sync to Apple Calendar instead
      </h2>

      <div className="mt-3 md:hidden">
        {subscriptionUrl && webcalFromHttps ? (
          <>
            <p className="text-base text-neutral-1">
              Apple Calendar can subscribe right from your phone — no computer
              required.
            </p>
            <Button asChild className="mt-3">
              <a href={webcalFromHttps}>
                <ExternalLink className="mr-2 size-4" />
                Open in Apple Calendar
              </a>
            </Button>
          </>
        ) : (
          <p className="text-sm text-neutral-2">
            Open this page from the Soonlist app and tap “Open in Apple
            Calendar,” or use the Apple Calendar button in the app&apos;s sync
            settings.
          </p>
        )}
      </div>

      <div className="mt-3 hidden md:block">
        {subscriptionUrl ? (
          <p className="text-base text-neutral-1">
            Open{" "}
            <strong className="text-neutral-0">
              Calendar → File → New Calendar Subscription
            </strong>
            , paste the link, click Subscribe. Right-click the new calendar →{" "}
            <strong className="text-neutral-0">Get Info</strong> → Auto-refresh
            → <strong className="text-neutral-0">Every 15 minutes</strong> for
            the freshest sync.
          </p>
        ) : (
          <p className="text-sm text-neutral-2">
            Open this page from the Soonlist app to get your subscription link,
            or sign in and copy it from your account settings.
          </p>
        )}
      </div>
    </section>
  );
}

function toWebcal(httpsUrl: string): string {
  return httpsUrl.replace(/^https:\/\//i, "webcal://");
}
